import { useHlHealth, useWsStatus } from '../hl/react';
import type { HealthStatus } from '../hl/health';
import type { WsState } from '../hl/ws';
import type { PlainKey } from '../i18n/i18n';
import { useT } from '../i18n/useT';

const VIEW: Record<WsState, { label: PlainKey; cls: string }> = {
  open: { label: 'shell.live.connected', cls: 'bg-yes' },
  connecting: { label: 'shell.live.connecting', cls: 'bg-warn animate-pulse' },
  reconnecting: { label: 'shell.live.reconnecting', cls: 'bg-warn animate-pulse' },
  closed: { label: 'shell.live.offline', cls: 'bg-danger' },
};

const HEALTH_LABEL: Record<HealthStatus, PlainKey> = {
  ok: 'shell.live.health_ok',
  degraded: 'shell.live.health_degraded',
  down: 'shell.live.health_down',
};

/**
 * Presentational dot for a given WS state and Hyperliquid health (#771). The colour follows the worse of the two:
 * health down = red, degraded = amber, otherwise the socket state.
 */
export function WsStatusView({ state, health = 'ok' }: { state: WsState; health?: HealthStatus }) {
  const t = useT();
  const v = VIEW[state];
  const label = t(v.label);
  const healthLabel = t(HEALTH_LABEL[health]);
  const cls = health === 'down' ? 'bg-danger' : health === 'degraded' ? 'bg-warn' : v.cls;
  return (
    <span
      className="flex items-center gap-1.5 text-xs text-muted"
      title={t('shell.live.tooltip', { state: label, health: healthLabel })}
      data-health={health}
      data-testid="hl-health-dot"
    >
      <span aria-hidden className={`inline-block h-2 w-2 rounded-full ${cls}`} />
      <span className="sr-only" data-testid="ws-status">
        {t('shell.live.sr', { state: label })}
      </span>
      <span className="sr-only" data-testid="hl-health">
        {healthLabel}
      </span>
    </span>
  );
}

/** Header indicator wired to the current environment's sockets and health monitor. */
export default function WsStatusDot() {
  return <WsStatusView state={useWsStatus()} health={useHlHealth()} />;
}
